import {Injectable} from '@angular/core';
import {createState, select, Store, withProps} from '@ngneat/elf';
import {UserRepository, UserSearchResult} from '@app/@core/repositories/user.repository';
import {tap} from 'rxjs/operators';
import {matchSorter} from 'match-sorter';
import {localStorageStrategy, persistState} from '@ngneat/elf-persist-state';
import {slice} from 'lodash-es';

export interface UserSearchState {
    params: { term: string };
    users: UserSearchResult[];
    recentUsers: UserSearchResult[];
}

const {state, config} = createState(withProps<UserSearchState>({
    params: {term: undefined},
    users: [],
    recentUsers: [],
}));

const store = new Store({name: 'userStore', state, config});

const getState = () => store.getValue();

const updateState = (newState: Partial<UserSearchState>) => {
    store.update(s => ({...s, ...newState}));
};

const persist = persistState(store, {
    key: 'userStore',
    storage: localStorageStrategy,
});

@Injectable({
    providedIn: 'root'
})
export class UserStoreService {
    users$ = store.pipe(select((s) => s.users));
    recentUsers$ = store.pipe(select(({recentUsers}) => recentUsers));

    constructor(private userRepository: UserRepository) {
    }

    getParams() {
        return getState().params;
    }

    search(term: string) {
        return this.userRepository.search(term).pipe(
            tap(list => {
                const users = matchSorter(list, term, {keys: ['fname', 'lname', 'email', 'mobile']}).slice(0, 15);
                updateState({
                    params: {term},
                    users
                });
            })
        );
    }

    addRecentUser(user: UserSearchResult) {
        const users = getState().recentUsers.filter(storeUser => storeUser.id !== user.id);
        const recentUsers = slice([user, ...users], 0, 15);
        updateState({recentUsers});
    }

    getRecentUsers(){
        return store.getValue().recentUsers;
    }
}
